import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Plus, Pencil, Trash2, UploadCloud, Image as ImageIcon } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import Select from '../../common/Select/Select'

/* Max logo size (px). Smaller images are also accepted.
   Keep in sync with MAX_WIDTH / MAX_HEIGHT in AdminFeaturedLogoController */
const LOGO_W = 400
const LOGO_H = 400

/* ── API (same base url / token handling as the Invoice page) ── */
const API_BASE = 'https://supadaa.com/api'

function getToken() {
  const keys = ['token', 'admin_token', 'adminToken', 'authToken', 'auth_token', 'access_token', 'accessToken']
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k)
    if (v) return v.replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '')
  }
  for (let i = 0; i < localStorage.length; i++) {
    try {
      const o = JSON.parse(localStorage.getItem(localStorage.key(i)))
      if (o && typeof o === 'object' && o.token) return o.token
    } catch (e) { /* not json */ }
  }
  return ''
}

/* body: undefined -> GET, FormData -> multipart POST, object -> JSON POST */
async function api(path, body) {
  const token = getToken()
  const isForm = body instanceof FormData
  const res = await fetch(API_BASE + path, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      Accept: 'application/json',
      ...(isForm || body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.status === false) {
    const first = json.errors ? Object.values(json.errors)[0]?.[0] : ''
    throw new Error(json.message || first || 'Request failed')
  }
  return json
}

const TYPE_OPTIONS = [
  { value: 'partner', label: 'Partner' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'media', label: 'Media' },
]
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t.label]))

const FILTER_TYPE_OPTIONS = [{ value: '', label: 'All Types' }, ...TYPE_OPTIONS]

const STATUS_OPTIONS = [
  { value: 1, label: 'active' },
  { value: 0, label: 'inactive' },
]

const EMPTY_FORM = {
  name: '', type: 'partner', link: '', sort_order: 0, status: 1,
  image: null,   // File (up to LOGO_W x LOGO_H)
}

export default function FeaturedLogo() {
  const [logos, setLogos] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [imageError, setImageError] = useState('')

  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const fileRef = useRef(null)
  const preview = useMemo(() => (form.image ? URL.createObjectURL(form.image) : null), [form.image])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  /* ── load ── */
  const load = async () => {
    setLoading(true)
    setLoadError('')
    try {
      const res = await api('/featured-logos')
      setLogos(res.data || [])
    } catch (e) {
      setLoadError(e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const filtered = logos.filter((l) => {
    const q = search.toLowerCase()
    if (q && !l.name.toLowerCase().includes(q)) return false
    if (typeFilter && l.type !== typeFilter) return false
    return true
  })

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }))

  /* ── open modals ── */
  const openAdd = () => {
    setActive(null)
    setForm({ ...EMPTY_FORM, sort_order: logos.length })
    setFormError('')
    setImageError('')
    setModalOpen(true)
  }

  const openEdit = (l) => {
    setActive(l)
    setForm({
      name: l.name, type: l.type, link: l.link || '',
      sort_order: l.sort_order, status: l.status, image: null,
    })
    setFormError('')
    setImageError('')
    setModalOpen(true)
  }

  /* ── pick image: up to LOGO_W x LOGO_H is accepted (smaller is fine) ── */
  const pickImage = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { setImageError('Please select a PNG, JPG or WEBP image'); return }
    if (file.size > 2 * 1024 * 1024) { setImageError('Logo must be smaller than 2MB'); return }

    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.naturalWidth > LOGO_W || img.naturalHeight > LOGO_H) {
        setImageError(`Size must be up to ${LOGO_W} × ${LOGO_H} px (any smaller size is fine). Your image: ${img.naturalWidth} × ${img.naturalHeight} px`)
      } else {
        setImageError('')
        setField('image', file)
      }
    }
    img.onerror = () => { URL.revokeObjectURL(url); setImageError('Could not read the image') }
    img.src = url
  }

  /* ── save / delete ── */
  const save = async () => {
    setFormError('')
    if (!form.name.trim()) { setFormError('Enter the name.'); return }
    if (!active && !form.image) { setImageError(`Upload a logo (max ${LOGO_W} × ${LOGO_H} px)`); return }

    const fd = new FormData()
    if (active) fd.append('id', active.id)
    fd.append('name', form.name.trim())
    fd.append('type', form.type)
    fd.append('link', form.link.trim())
    fd.append('sort_order', form.sort_order === '' ? 0 : form.sort_order)
    fd.append('status', form.status)
    if (form.image instanceof File) fd.append('image', form.image)

    setSaving(true)
    try {
      const res = await api(active ? '/featured-logos/update' : '/featured-logos/create', fd)
      const row = res.data
      setLogos((p) => {
        const next = active ? p.map((x) => (x.id === row.id ? row : x)) : [...p, row]
        return next.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
      })
      setModalOpen(false)
    } catch (e) {
      setFormError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    try {
      await api('/featured-logos/delete', { id: active.id })
      setLogos((p) => p.filter((x) => x.id !== active.id))
    } catch (e) {
      window.alert(e.message)
    }
    setDeleteOpen(false)
    setActive(null)
  }

  const shownImage = preview || (active && active.image) || null

  return (
    <div className="dash-page page-in">
      <div className="pg-header">
        <h1 className="page-title" style={{ margin: 0 }}>Featured In</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Logo</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <label className="tb-search">
            <Search size={15} aria-hidden="true" />
            <input type="text" placeholder="Search by name…" aria-label="Search logos"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <Select
            className="cat-filter-select"
            ariaLabel="Type filter"
            value={typeFilter}
            onChange={setTypeFilter}
            options={FILTER_TYPE_OPTIONS}
          />
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>Logo</th><th>Name</th><th>Type</th><th>Link</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>}
              {!loading && loadError && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#EF4444' }}>{loadError}</td></tr>
              )}
              {!loading && !loadError && filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>No logos found</td></tr>
              )}
              {!loading && filtered.map((l) => (
                <tr key={l.id}>
                  <td>
                    <div className="fl-thumb"><img src={l.image} alt={l.name} /></div>
                  </td>
                  <td style={{ fontWeight: 600, color: 'var(--text1)' }}>{l.name}</td>
                  <td><span className="badge badge-gold">{TYPE_LABEL[l.type] || l.type}</span></td>
                  <td style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.link ? <a href={l.link} target="_blank" rel="noreferrer">{l.link}</a> : '—'}
                  </td>
                  {/* <td>{l.sort_order}</td> */}
                  <td>
                    <span className={`badge ${l.status === 1 ? 'badge-active' : 'badge-inactive'}`}>
                      {l.status === 1 ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(l)} aria-label="Edit logo"><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(l); setDeleteOpen(true) }} aria-label="Delete logo"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ADD / EDIT ── */}
      <Modal
        open={modalOpen}
        title={active ? 'Edit Logo' : 'Add Logo'}
        subtitle="Shown in the “Featured In” slider on the home page"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : active ? 'Update Logo' : 'Add Logo'}
            </button>
          </>
        )}
      >
        {formError && <div className="login-error" style={{ marginBottom: 12 }}>{formError}</div>}

        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Name</label>
            <input className="input" placeholder="e.g. Mid-day" value={form.name}
              onChange={(e) => setField('name', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Type</label>
            <Select value={form.type} onChange={(v) => setField('type', v)} options={TYPE_OPTIONS} />
          </div>

          <div className="form-field full">
            <label>Link <small style={{ color: 'var(--text3)' }}>(optional · opens when the logo is clicked)</small></label>
            <input className="input" placeholder="https://example.com" value={form.link}
              onChange={(e) => setField('link', e.target.value)} />
          </div>

          <div className="form-field">
            <label>Sort Order <small style={{ color: 'var(--text3)' }}>(lower comes first)</small></label>
            <input type="number" min={0} className="input" value={form.sort_order}
              onChange={(e) => setField('sort_order', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Status</label>
            <Select value={form.status} onChange={(v) => setField('status', Number(v))} options={STATUS_OPTIONS} />
          </div>

          <div className="form-field full">
            <label>
              Logo <small style={{ color: 'var(--text3)' }}>(any size up to {LOGO_W} × {LOGO_H} px · PNG, JPG, WEBP · max 2MB)</small>
            </label>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pickImage} />

            <div className="fl-box">
              {shownImage ? (
                <img src={shownImage} alt="Logo preview" />
              ) : (
                <div className="fl-empty"><ImageIcon size={22} /><span>No logo uploaded</span></div>
              )}
              {preview && <span className="fl-new">New</span>}
            </div>

            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()}>
                <UploadCloud size={13} /> {shownImage ? 'Replace logo' : 'Upload logo'}
              </button>
              {form.image && (
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => { setField('image', null); setImageError('') }}>Cancel new</button>
              )}
            </div>
            {imageError && <small style={{ color: '#ef4444', display: 'block', marginTop: 4 }}>{imageError}</small>}
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete logo"
        message={`Delete ${active?.name || 'this logo'}? It will be removed from the home page.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        .pg-header { display: flex; align-items: center; justify-content: space-between; gap: var(--space-md); flex-wrap: wrap; }

        .tb-search {
          flex: 1 1 260px; min-width: 0; margin: 0;
          display: flex; align-items: center; gap: 10px;
          height: 38px; padding: 0 12px; cursor: text;
          border: 1px solid var(--border); border-radius: 10px;
          background: #f6f8ff; color: var(--text3);
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        .tb-search:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.16); }
        .tb-search svg { flex-shrink: 0; }
        .tb-search input {
          flex: 1; min-width: 0; height: 100%; padding: 0; margin: 0;
          border: 0; outline: 0; box-shadow: none; background: transparent;
          font: inherit; font-size: var(--fs-sm); color: var(--text1);
        }
        .sel-trigger.cat-filter-select { width: auto; min-width: 160px; flex-shrink: 0; }

        .login-error {
          background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5; font-size: 0.875rem; padding: 0.6rem 0.9rem; border-radius: 10px;
        }

        /* table thumbnail: same ratio as the max logo size */
        .fl-thumb {
          width: 104px; height: 40px; border-radius: var(--radius-md);
          border: 1px solid var(--border); background: #fff; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
        }
        .fl-thumb img { width: 100%; height: 100%; object-fit: contain; display: block; }

        /* modal preview */
        .fl-box {
          position: relative; width: 100%; max-width: 360px; aspect-ratio: 2 / 1;
          border: 1px dashed var(--border-active, var(--border)); border-radius: var(--radius-md);
          background: #fff; overflow: hidden;
        }
        .fl-box img { width: 100%; height: 100%; object-fit: contain; display: block; }
        .fl-empty {
          position: absolute; inset: 0; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 6px;
          color: var(--text3); font-size: var(--fs-xs);
        }
        .fl-new {
          position: absolute; left: 6px; bottom: 6px; padding: 2px 8px; border-radius: 10px;
          font-size: 10px; font-weight: 600; background: rgba(16, 185, 129, .85); color: #fff;
        }
      `}</style>
    </div>
  )
}